import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  getRequest,
  postRequest,
  deleteRequest,
  patchRequest,
  patchFileRequest,
  getBlobRequest
} from "../helpers/fetchHandler"
import type { Destination, Guide, PaginationInfo } from "../constants/types"

type DestinationWithGuides = Destination & { guides: Guide[] }

type ListParams = {
  page?: number
  limit?: number
  search?: string
}

const destinationKeys = {
  all: ["destinations"] as const,
  list: (params: { page: number; limit: number; search: string }) => ["destinations", "list", params] as const,
  detail: (id: string | undefined) => ["destination", id] as const,
  example: ["exampleDestination"] as const
}

export function useDestinations({ page = 1, limit = 50, search = "" }: ListParams) {
  return useQuery({
    queryKey: destinationKeys.list({ page, limit, search }),
    queryFn: async () => {
      const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() })
      if (search) params.append("search", search)
      return getRequest({ url: `/api/v2/destinations?${params}`, auth: true }) as Promise<{
        data: Destination[]
        pagination: PaginationInfo
      }>
    }
  })
}

export function useDestination(id: string | undefined) {
  return useQuery({
    queryKey: destinationKeys.detail(id),
    queryFn: () => getRequest({ url: `/api/v1/destinations/destination/${id}`, auth: true }) as Promise<Destination>,
    enabled: !!id
  })
}

export function useExampleDestination() {
  return useQuery({
    queryKey: destinationKeys.example,
    queryFn: () => getRequest({ url: "/api/v1/destinations/traveller/EXAMPLE" }) as Promise<DestinationWithGuides>,
    staleTime: 60 * 60 * 1000
  })
}

export function useCreateDestination() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      postRequest({ url: "/api/v1/destinations", data, auth: true }) as Promise<Destination>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: destinationKeys.all })
    }
  })
}

export function useUpdateDestination() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      patchRequest({ url: `/api/v1/destinations/${id}`, data, auth: true }) as Promise<Destination>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: destinationKeys.all })
    }
  })
}

export function useUpdateDestinationWithFile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      const formData = new FormData()
      Object.entries(data).forEach(([key, value]) => {
        if (value != null && typeof value === "object" && "name" in (value as object)) {
          formData.append(key, value as Blob, (value as File).name)
        } else if (value !== undefined) {
          formData.append(key, String(value))
        }
      })
      return patchFileRequest({
        url: `/api/v1/destinations/${id}`,
        data: formData,
        auth: true
      }) as Promise<Destination>
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(destinationKeys.detail(variables.id), data)
      queryClient.invalidateQueries({ queryKey: destinationKeys.all })
    }
  })
}

export function useDeleteDestination() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteRequest({ url: `/api/v1/destinations/${id}/`, auth: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: destinationKeys.all })
    }
  })
}

export function useDownloadPdf() {
  return useMutation({
    mutationFn: async ({ destinationId, type }: { destinationId: string; type: "guidebook" | "brochure" }) => {
      const { blob, filename } = await getBlobRequest({
        url: `/api/v1/pdf/destination/${destinationId}/${type}`,
        auth: true
      })
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = blobUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
    }
  })
}
